class AffiliateFlow {
  static _fetchPromise = null;
  static _affiliateData = null;
  static PENDING_REFERRAL_TTL_MS = 60 * 60 * 1000; // 1 hour; keep in sync with affiliate-market-redirect.liquid
  static INVALID_REFERRER_TTL_MS = 15 * 60 * 1000; // Same lifetime as the partner lookup cache
  static INVALID_REFERRER_STATUSES = [400, 404];
  static AFFILIATE_MARKET_ROOT = '/en-provider'; // Keep in sync with affiliate-market-redirect.liquid

  static KEYS = {
    AFFILIATE_LOCATIONS: 'affiliate_locations',
    SHOP_ACCESS_GRANTED: 'shop_access_granted',
    PREVIOUS_ORDER_ATTRIBUTE: 'previous_order_attribute',
    PENDING_REFERRAL: 'pending_referral',
    INVALID_REFERRERS: 'invalid_referrers', // Keep in sync with affiliate-market-redirect.liquid

    // URL parameter and dataset keys
    PARAM_QR_CODE_ID: 'qr_code_id',
    PARAM_LOCATION_ID: 'location_id',
    PARAM_REFERRER: 'referrer',
    PARAM_TRACKING_SOURCE: 'tracking_source',
    PARAM_PRODUCT_ID: 'product_id',
    PARAM_VARIANT_ID: 'variant_id',
    PARAM_SELLING_PLAN_ID: 'selling_plan_id',
    PARAM_QUANTITY: 'quantity',

    // Tracking source values
    SOURCE_ORGANIC: 'organic',
    SOURCE_UNITY_QR_CODE_SCAN: 'unity_qr_code_scan',
    SOURCE_AFFILIATE: 'affiliate_referral',
    SOURCE_MOBILE: 'mobile_app',
  };

  static init() {
    const run = async () => {
      await this.syncReferralParamsToCart(); // Ensure any referral parameters are synced to the cart attributes and header data attributes on page load
      await this.handleReferralFlow(); // This sets _affiliateData which is needed for the banner content

      this.ensureAffiliateLocationUI(); // Set up the affiliate banner in the header if it's not already present
      this.updateAffiliateBannerContent(); // Update banner content after fetching affiliate data

      this.setupATCButtons(); // Set up event listeners for "Add to Cart" buttons in affiliate flow
      this.reapplyPreviousOrderAttributes(); // Reapply previous order attributes to maintain affiliate context across sessions
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  }

  /**
   * The referral id in the current URL, trimmed. Any non-empty value is accepted — the format is
   * deliberately not validated, matching snippets/affiliate-market-redirect.liquid. An "?referrer="
   * with no value reads as no referral.
   * Security: an accepted referral is not an authorized one. This value reaches a cart attribute
   * and the affiliate lookup endpoint, so the backend remains the only real check.
   * @returns {string|null}
   */
  static getUrlReferrer() {
    const value = this.params.get(this.KEYS.PARAM_REFERRER);

    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
  }

  /**
   * The referral UUID persisted on the cart (cart.attributes.referrer). window.themeAffiliate is
   * rendered server-side in <head> (snippets/scripts-and-styles.liquid), so it is present on every
   * template before this deferred script runs; the header data attribute is only a fallback for
   * pages that render without the header section.
   * @returns {string|null}
   */
  static getCartReferrer() {
    const fromTheme = window.themeAffiliate?.cartReferrer;
    if (typeof fromTheme === 'string' && fromTheme.trim() !== '') return fromTheme.trim();

    return document.querySelector('header')?.dataset.referrerId?.trim() || null;
  }

  /**
   * Referral params saved for this tab that the cart has not confirmed yet, or null (none / expired / storage
   * blocked). Lets a later page retry the cart update when the visitor navigated before it finished.
   * @returns {{referrer: string|null, location_id: string|null, qr_code_id: string|null, savedAt: number}|null}
   */
  static getPendingReferral() {
    try {
      const pending = JSON.parse(sessionStorage.getItem(this.KEYS.PENDING_REFERRAL) || 'null');

      if (!pending || !(Date.now() - pending.savedAt < this.PENDING_REFERRAL_TTL_MS)) {
        sessionStorage.removeItem(this.KEYS.PENDING_REFERRAL);
        return null;
      }

      return pending;
    } catch (_) {
      return null;
    }
  }

  static savePendingReferral({ referrer, location_id, qr_code_id }) {
    try {
      const pending = { referrer: referrer || null, location_id: location_id || null, qr_code_id: qr_code_id || null };
      sessionStorage.setItem(this.KEYS.PENDING_REFERRAL, JSON.stringify({ ...pending, savedAt: Date.now() }));
    } catch (_) {}
  }

  static clearPendingReferral() {
    try {
      sessionStorage.removeItem(this.KEYS.PENDING_REFERRAL);
    } catch (_) {}
  }

  /**
   * Referral ids the partner lookup rejected in this tab, mapped to their expiry timestamp.
   * The render-blocking redirect in snippets/affiliate-market-redirect.liquid reads the same key so it
   * never sends a visitor with a rejected id back into the affiliate market.
   * @returns {Object<string, number>}
   */
  static getInvalidReferrers() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.KEYS.INVALID_REFERRERS) || '{}');

      return stored && typeof stored === 'object' ? stored : {};
    } catch (_) {
      return {};
    }
  }

  /**
   * Whether the partner lookup rejected this referral id (unexpired) in this tab.
   * @param {string|null} referrerId - Referral id to check.
   * @returns {boolean}
   */
  static isReferrerInvalid(referrerId) {
    if (!referrerId) return false;

    const expiresAt = this.getInvalidReferrers()[referrerId];
    return typeof expiresAt === 'number' && Date.now() < expiresAt;
  }

  /**
   * Remember a rejected referral id for this tab, pruning expired entries.
   * @param {string} referrerId - Referral id the partner lookup rejected.
   */
  static markReferrerInvalid(referrerId) {
    if (!referrerId) return;

    try {
      const now = Date.now();
      const entries = Object.entries(this.getInvalidReferrers()).filter(([, expiresAt]) => expiresAt > now);

      const invalid = Object.fromEntries(entries);

      invalid[referrerId] = now + this.INVALID_REFERRER_TTL_MS;
      sessionStorage.setItem(this.KEYS.INVALID_REFERRERS, JSON.stringify(invalid));
    } catch (_) {}
  }

  /**
   * Tracking source for a combination of referral params. Shared by the referral sync and the
   * invalid-referral fallback so both classify a visit the same way.
   * @param {{qrCodeId: string|null, locationId: string|null, affiliateId: string|null}} context
   * @returns {string}
   */
  static resolveTrackingSource({ qrCodeId, locationId, affiliateId }) {
    const { SOURCE_ORGANIC, SOURCE_UNITY_QR_CODE_SCAN, SOURCE_AFFILIATE, SOURCE_MOBILE } = this.KEYS;

    if (qrCodeId && locationId) return SOURCE_UNITY_QR_CODE_SCAN;
    if (affiliateId && !qrCodeId) return SOURCE_AFFILIATE;
    if (locationId && !affiliateId && !qrCodeId) return SOURCE_MOBILE;
    return SOURCE_ORGANIC;
  }

  /**
   * The current URL moved out of the affiliate market (/en-provider) with the referrer param removed,
   * so the page re-renders with standard DTC pricing. Other params and the hash are kept.
   * @returns {string}
   */
  static getDtcUrl() {
    const url = new URL(window.location.href);
    const root = this.AFFILIATE_MARKET_ROOT;

    url.searchParams.delete(this.KEYS.PARAM_REFERRER);

    if (url.pathname === root || url.pathname.startsWith(`${root}/`)) {
      url.pathname = url.pathname.slice(root.length) || '/';
    }

    return url.toString();
  }

  /**
   * Silently drop a referral the partner lookup rejected and fall back to the standard DTC experience:
   * clear it from the cart and the pending copy, then reload the same page outside /en-provider. No
   * message is shown — the visitor simply lands on normal pricing.
   * @returns {Promise<boolean>} True when the page is navigating away, false when it already shows DTC.
   */
  static async revertToDtcExperience() {
    const { PARAM_REFERRER, PARAM_TRACKING_SOURCE } = this.KEYS;

    this.clearPendingReferral();

    try {
      const response = await fetchData({
        ...SHOPIFY_API.UPDATE_CART_ITEM,
        body: JSON.stringify({
          attributes: {
            [PARAM_REFERRER]: null,
            [PARAM_TRACKING_SOURCE]: this.resolveTrackingSource({
              qrCodeId: this.getQrCodeId(),
              locationId: this.getLocationId(),
              affiliateId: null,
            }),
          },
        }),
        keepalive: true, // Ensure the request completes even if the page is unloading
      });

      if (!response.ok) console.warn('[AffiliateFlow] Failed to clear invalid referral from cart:', response.status);
    } catch (error) {
      console.error('[AffiliateFlow] Failed to clear invalid referral from cart:', error);
    }

    window.themeAffiliate = window.themeAffiliate || {};
    window.themeAffiliate.cartReferrer = null;

    const header = document.querySelector('header');
    if (header) delete header.dataset.referrerId;

    // Only navigate when the URL changes; reloading the same URL could loop if the cart update failed.
    const dtcUrl = this.getDtcUrl();
    if (dtcUrl === window.location.href) return false;

    window.location.replace(dtcUrl);
    return true;
  }

  // True when the server-rendered header already carries every pending value (the cart update landed).
  static isPendingReferralInCart(pending) {
    const dataset = document.querySelector('header')?.dataset || {};
    const pairs = [
      [pending.referrer, dataset.referrerId],
      [pending.location_id, dataset.locationId],
      [pending.qr_code_id, dataset.qrCodeId],
    ];
    return pairs.every(([wanted, actual]) => !wanted || wanted === (actual || '').trim());
  }

  /**
   * Sync referral parameters (affiliate ID, location ID, QR code ID) to cart attributes on page load.
   * URL params win; otherwise a pending referral from an earlier page is retried until the cart confirms it, so
   * clicking a link before the first update finished no longer loses the referral. Never throws: a failure
   * keeps the pending copy for the next page instead of aborting the rest of init().
   */
  static async syncReferralParamsToCart() {
    const { PARAM_QR_CODE_ID, PARAM_LOCATION_ID, PARAM_REFERRER, PARAM_TRACKING_SOURCE } = this.KEYS;

    let qrCodeId = this.params.get(PARAM_QR_CODE_ID);
    let locationId = this.params.get(PARAM_LOCATION_ID);
    let affiliateId = this.getUrlReferrer();

    // A referral already rejected in this tab is never written back to the cart.
    if (this.isReferrerInvalid(affiliateId)) affiliateId = null;

    if (qrCodeId || locationId || affiliateId) {
      this.savePendingReferral({ referrer: affiliateId, location_id: locationId, qr_code_id: qrCodeId });
    } else {
      const pending = this.getPendingReferral();
      if (!pending) return;

      if (this.isPendingReferralInCart(pending)) {
        this.clearPendingReferral();
        return;
      }

      ({ referrer: affiliateId, location_id: locationId, qr_code_id: qrCodeId } = pending);
      if (this.isReferrerInvalid(affiliateId)) affiliateId = null;
    }

    // Clear any previous post-order attributes to avoid accidentally applying them to the wrong session
    try {
      sessionStorage.removeItem(this.KEYS.PREVIOUS_ORDER_ATTRIBUTE);
    } catch (_) {}

    const attributes = {
      [PARAM_QR_CODE_ID]: qrCodeId || null,
      [PARAM_LOCATION_ID]: locationId || null,
      [PARAM_REFERRER]: affiliateId || null,
      [PARAM_TRACKING_SOURCE]: this.resolveTrackingSource({ qrCodeId, locationId, affiliateId }),
    };

    try {
      const updateCartResponse = await fetchData({
        ...SHOPIFY_API.UPDATE_CART_ITEM,
        body: JSON.stringify({ attributes, sections: 'header' }),
        keepalive: true,
      });

      const updateCartResult = await updateCartResponse.json().catch(() => null);

      if (!updateCartResponse.ok) {
        console.error('[AffiliateFlow] Failed to update cart with referral params:', updateCartResult);
        return;
      }

      this.applyHeaderDataAttributes(updateCartResult?.sections?.header);
      this.clearPendingReferral();
    } catch (error) {
      console.error('[AffiliateFlow] Failed to update cart with referral params:', error);
    }
  }

  static applyHeaderDataAttributes(sectionHtml) {
    if (!sectionHtml) return;

    const headerElement = document.querySelector('header');
    if (!headerElement) return;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newHeader = doc.querySelector('header');
    if (!newHeader) return;

    // Mirror every data-* attribute from the server-rendered header to the live one
    Object.entries(newHeader.dataset).forEach(([key, value]) => {
      headerElement.dataset[key] = value;
    });

    window.themeAffiliate = window.themeAffiliate || {};
    window.themeAffiliate.cartReferrer = headerElement.dataset.referrerId || null;
  }

  // Main entry point - determines if user is a referred/affiliate user and applies necessary UI changes and redirects
  static async handleReferralFlow() {
    try {
      const { SOURCE_MOBILE, SOURCE_UNITY_QR_CODE_SCAN, SOURCE_ORGANIC, SOURCE_AFFILIATE } = this.KEYS;

      const affiliateId = this.getReferralCandidate();

      let data = null;
      if (affiliateId) {
        data = await this.fetchAffiliateData(affiliateId);
      }

      // Wrong partner id: silently fall back to DTC pricing on the normal storefront (no banner/message).
      if (this.isReferrerInvalid(affiliateId)) {
        const isNavigating = await this.revertToDtcExperience();
        if (isNavigating) return null;
        data = null;
      }

      const source = this.getSource();
      const locationId = this.getLocationId();

      const hasDirectAccess = source === SOURCE_MOBILE || source === SOURCE_UNITY_QR_CODE_SCAN;
      const isOrganicWithLocation = source === SOURCE_ORGANIC && !!locationId;

      this._affiliateData = data;

      const locationCount = this.getLocationCount(data);

      // Referral users with a location ID but more than 1 location should see the form but not the locations section
      const isReferralWithMultipleLocations = source === SOURCE_AFFILIATE && !!locationId && locationCount > 1;

      this.applyAffiliateUI({ hasDirectAccess, locationCount, isOrganicWithLocation, isReferralWithMultipleLocations });

      // Multiple or zero locations (or a failed lookup): keep the Locations links visible.
      if (!(hasDirectAccess || locationCount === 1)) this.showLocationElements();

      return data;
    } catch (error) {
      console.error('[AffiliateFlow] run failed:', error);
      return null;
    }
  }

  /**
   * Fetch pricing plans from an external API URL configured on a section.
   * Merges affiliate/tracking context (referrer, location, QR code, tracking source)
   * from the current session so plans can be personalised server-side.
   * @param {string} apiUrl - Absolute API URL
   * @param {Object<string, string|number|null>} [extraParams] - Extra query params to append.
   * @returns {Promise<object|null>} Parsed JSON payload on success, null on failure.
   */
  static async fetchPricingPlans(apiUrl, extraParams = {}) {
    if (!apiUrl) return null;

    try {
      const url = new URL(apiUrl, window.location.origin);
      const { PARAM_REFERRER, PARAM_LOCATION_ID, PARAM_QR_CODE_ID, PARAM_TRACKING_SOURCE } = this.KEYS;

      const context = {
        [PARAM_REFERRER]: this.getAffiliateId(),
        [PARAM_LOCATION_ID]: this.getLocationId(),
        [PARAM_QR_CODE_ID]: this.getQrCodeId(),
        [PARAM_TRACKING_SOURCE]: this.getSource(),
        ...extraParams,
      };

      for (const [key, value] of Object.entries(context)) {
        if (value === null || value === undefined || value === '') continue;
        url.searchParams.set(key, String(value));
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        console.warn('[AffiliateFlow] fetchPricingPlans non-OK status:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('[AffiliateFlow] fetchPricingPlans failed:', error);
      return null;
    }
  }

  /**
   * Fetch affiliate data from API with two-tier caching (TTL: 15 minutes).
   * On TTL expiry or corruption the sessionStorage entry is purged and fresh data is fetched.
   */
  static async fetchAffiliateData(affiliateId) {
    if (this._fetchPromise) return this._fetchPromise;
    if (this.isReferrerInvalid(affiliateId)) return null;

    const storageKey = this.KEYS.AFFILIATE_LOCATIONS + '_' + affiliateId;
    const TTL_MS = 15 * 60 * 1000; // 15 minutes

    try {
      const cached = sessionStorage.getItem(storageKey);

      if (cached) {
        const { data, expiresAt } = JSON.parse(cached);

        if (data && Date.now() < expiresAt) {
          return data;
        }

        sessionStorage.removeItem(storageKey);
      }
    } catch (e) {
      console.warn('[AffiliateFlow] Corrupted sessionStorage data, re-fetching:', e);
      try {
        sessionStorage.removeItem(storageKey);
      } catch (_) {}
    }

    // API fetch — store the promise so any concurrent caller awaits the same request
    this._fetchPromise = (async () => {
      try {
        const res = await fetchData(STYKU_API.AFFILIATE_PARTNER_LOCATIONS(affiliateId));

        // The partner does not exist (malformed or unknown id): remember it so the visit falls back to DTC.
        if (this.INVALID_REFERRER_STATUSES.includes(res?.status)) {
          this.markReferrerInvalid(affiliateId);
          return null;
        }

        if (!res?.ok) return null;

        const data = await res.json();

        sessionStorage.setItem(storageKey, JSON.stringify({ data, expiresAt: Date.now() + TTL_MS }));
        sessionStorage.setItem(this.KEYS.SHOP_ACCESS_GRANTED, 'true');

        return data;
      } catch (e) {
        console.error('[AffiliateFlow] API error:', e);
        return null;
      } finally {
        this._fetchPromise = null;
      }
    })();

    return this._fetchPromise;
  }

  // Cache URLSearchParams access
  static get params() {
    return new URLSearchParams(window.location.search);
  }

  // Helper to get value from URL params or dataset with fallback
  static getParamOrData(paramKey, dataKey) {
    return this.params.get(paramKey) || document.querySelector('header')?.dataset[dataKey] || null;
  }

  /**
   * Raw referral id for the current session: the URL referral link if present, otherwise the referral
   * persisted on the cart, otherwise a pending one. Not yet checked against the partner lookup.
   * @returns {string|null}
   */
  static getReferralCandidate() {
    return this.getUrlReferrer() || this.getCartReferrer() || this.getPendingReferral()?.referrer || null;
  }

  /**
   * Affiliate ID for the current session (see getReferralCandidate), minus any id the partner lookup
   * already rejected in this tab. Any other non-empty value starts an affiliate session (banner,
   * locations lookup, pricing context) until the lookup endpoint rejects it.
   * @returns {string|null}
   */
  static getAffiliateId() {
    const candidate = this.getReferralCandidate();
    return candidate && !this.isReferrerInvalid(candidate) ? candidate : null;
  }

  // Location ID can come from URL or dataset
  static getLocationId() {
    return this.getParamOrData(this.KEYS.PARAM_LOCATION_ID, 'locationId');
  }

  // QR code ID can come from URL or dataset
  static getQrCodeId() {
    return this.getParamOrData(this.KEYS.PARAM_QR_CODE_ID, 'qrCodeId');
  }

  static getSource() {
    return this.getParamOrData(this.KEYS.PARAM_TRACKING_SOURCE, 'trackingSource');
  }

  /**
   * Number of locations linked to the partner. Falls back to `locations.length` when the API omits or
   * mis-types `locationCount`, so a single-location partner is still detected.
   * @param {object|null} data - Partner payload from fetchAffiliateData().
   * @returns {number}
   */
  static getLocationCount(data) {
    const raw = data?.locationCount;
    const count = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
    if (Number.isFinite(count)) return count;

    return Array.isArray(data?.locations) ? data.locations.length : 0;
  }

  // Locations menu items: header (top-level, sub-menu, utility) and footer render `link-{{ link.handle }}`.
  static LOCATION_LINK_SELECTOR = '.link-locations';

  static hideLocationElements() {
    document.documentElement.classList.add('affiliate-hide-locations');
    document.querySelectorAll(this.LOCATION_LINK_SELECTOR).forEach((el) => el.classList.add('affiliate-hide'));
  }

  // Show the Locations links again (multi-location or no-location partners); also undoes a stale early hide
  static showLocationElements() {
    document.documentElement.classList.remove('affiliate-hide-locations');
    document.querySelectorAll(this.LOCATION_LINK_SELECTOR).forEach((el) => el.classList.remove('affiliate-hide'));
  }

  // Update "Book" or "Scan" buttons to point to pricing page for affiliate users
  static updateButtons() {
    const pricingUrl = PAGE_URLS?.PRICING || '/pricing';

    document.querySelectorAll('a[href*="location"]').forEach((btn) => {
      if (this.isBookScanButton(btn)) {
        btn.href = pricingUrl;
        btn.dataset.affiliateModified = 'true';
      }
    });
  }

  // Identify if a button is a "Book" or "Scan" button based on its text
  static isBookScanButton(el) {
    const text = el.textContent?.toLowerCase() || '';
    return text.includes('book') || text.includes('scan');
  }

  // Hide the mockup "Add to Cart" buttons that are shown to non-affiliate users on product pages
  static hideMockupButtons() {
    document.querySelectorAll('.mockup-button-block').forEach((el) => {
      el.style.display = 'none';
    });
  }

  // Show the actual form wrappers that contain the product forms for affiliate users
  static showFormWrappers() {
    document.querySelectorAll('.form-wrapper').forEach((el) => {
      el.classList.remove('hidden');
    });
  }

  // Redirect affiliate users to locations page if they try to access the locations page directly
  static redirectIfNeeded() {
    const path = window.location.pathname;
    // Normalize to a pathname so the market/locale prefix is preserved on both sides.
    const locationsPath = toPathname(PAGE_URLS?.LOCATIONS || '/pages/locations');
    const pricingUrl = PAGE_URLS?.PRICING || '/pages/pricing';

    if (path.includes(locationsPath)) {
      window.location.replace(pricingUrl);
    }
  }

  // Set up event listeners for "Add to Cart" buttons in affiliate flow
  static setupATCButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.mockup-button-block .btn-atc');
      if (!btn) return;

      e.preventDefault();

      const form = btn.closest('.buy-now-button-block')?.querySelector('.form-wrapper');

      this.redirectToLocationPage(form?.dataset ?? {});
    });
  }

  // Redirect to locations page with product, variant, and selling plan info for affiliate users.
  static redirectToLocationPage({ sellingPlanId, productId, variantId, quantity } = {}) {
    const url = new URL(PAGE_URLS?.LOCATIONS || '/pages/locations', window.location.origin);

    if (productId && variantId) {
      url.searchParams.set(this.KEYS.PARAM_PRODUCT_ID, productId);
      url.searchParams.set(this.KEYS.PARAM_VARIANT_ID, variantId);

      if (sellingPlanId) {
        url.searchParams.set(this.KEYS.PARAM_SELLING_PLAN_ID, sellingPlanId);
      }

      // Carry multi-unit quantity (e.g. a 3-scan pack) so the location page adds the right count. Omit for 1.
      const qty = Number(quantity);
      if (qty > 1) {
        url.searchParams.set(this.KEYS.PARAM_QUANTITY, qty);
      }
    }

    window.location.href = url.toString();
  }

  // Get redirect data (product, variant, selling plan) from URL params for affiliate users coming from product page
  static getRedirectDataFromSearchParams() {
    const { PARAM_PRODUCT_ID, PARAM_VARIANT_ID, PARAM_SELLING_PLAN_ID, PARAM_QUANTITY } = this.KEYS;

    const params = this.params;

    return {
      productId: params.get(PARAM_PRODUCT_ID),
      variantId: params.get(PARAM_VARIANT_ID),
      sellingPlanId: params.get(PARAM_SELLING_PLAN_ID),
      quantity: params.get(PARAM_QUANTITY),
    };
  }

  // Get previous order attributes from session storage to apply
  static getPreviousOrderAttributesFromSession() {
    const previousAttrs = sessionStorage.getItem(this.KEYS.PREVIOUS_ORDER_ATTRIBUTE);
    return previousAttrs ? JSON.parse(previousAttrs) : null;
  }

  // Reapply previous order attributes to the current session to maintain affiliate context across sessions
  static reapplyPreviousOrderAttributes() {
    const previousAttrs = this.getPreviousOrderAttributesFromSession();

    if (!previousAttrs) return;

    const source = previousAttrs[this.KEYS.PARAM_TRACKING_SOURCE];
    const locationId = previousAttrs[this.KEYS.PARAM_LOCATION_ID];
    const affiliateId = previousAttrs[this.KEYS.PARAM_REFERRER];

    const hasDirectAccess = source === this.KEYS.SOURCE_MOBILE || source === this.KEYS.SOURCE_UNITY_QR_CODE_SCAN;
    const isOrganicWithLocation = source === this.KEYS.SOURCE_ORGANIC && !!locationId;

    let locationCount = 0;
    if (affiliateId) {
      try {
        const cached = sessionStorage.getItem(this.KEYS.AFFILIATE_LOCATIONS + '_' + affiliateId);
        if (cached) {
          const { data } = JSON.parse(cached);
          locationCount = this.getLocationCount(data);
        }
      } catch (_) {}
    }

    // Referral users with a location ID but more than 1 location should see the form but not the locations section
    const isReferralWithMultipleLocations = source === this.KEYS.SOURCE_AFFILIATE && locationCount > 1;

    // Apply the same affiliate UI logic as on initial load based on the previous order attributes
    this.applyAffiliateUI({ hasDirectAccess, locationCount, isOrganicWithLocation, isReferralWithMultipleLocations });
  }

  /**
   * Apply affiliate-related UI changes based on user context:
   * @param {boolean} hasDirectAccess - Whether the user has direct access via mobile app or QR code scan
   * @param {number} locationCount - Number of locations associated with the affiliate (if applicable)
   * @param {boolean} isOrganicWithLocation - Whether the user is an organic visitor with a location ID
   * @param {boolean} isReferralWithMultipleLocations - Whether the user is a referral with a location ID and multiple locations available
   */
  static applyAffiliateUI({ hasDirectAccess, locationCount, isOrganicWithLocation, isReferralWithMultipleLocations }) {
    if (hasDirectAccess || locationCount === 1) {
      document.body.classList.add('affiliate-user');

      this.hideMockupButtons();
      this.showFormWrappers();
      this.hideLocationElements();
      this.updateButtons();
      this.redirectIfNeeded();
    } else if (isOrganicWithLocation || isReferralWithMultipleLocations) {
      document.body.classList.add('affiliate-user');

      this.hideMockupButtons();
      this.showFormWrappers();
    }
  }

  // Render affiliate location experience for referred users based on their affiliate data
  static ensureAffiliateLocationUI() {
    const affiliateId = this.getAffiliateId();

    // No affiliate ID means no referral context, so we don't need to show the banner
    if (!affiliateId) return;

    // Respect the user's dismissal for the rest of this browser session
    if (sessionStorage.getItem('affiliate-location-banner-dismissed') === '1') return;

    if (!document.getElementById('affiliate-location-banner')) {
      const banner = document.createElement('div');
      banner.id = 'affiliate-location-banner';

      banner.innerHTML = `
      <div class="affiliate-location-banner-inner text-primary-gradient container">
        <div id="affiliate-location-banner-text"></div>
      </div>
      <button
        id="affiliate-banner-close"
        type="button"
        aria-label="Dismiss"
        class="affiliate-location-banner-close text-secondary"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.46967 5.46967C5.76256 5.17678 6.23744 5.17678 6.53033 5.46967L12 10.9393L17.4697 5.46967C17.7626 5.17678 18.2374 5.17678 18.5303 5.46967C18.8232 5.76256 18.8232 6.23744 18.5303 6.53033L13.0607 12L18.5303 17.4697C18.8232 17.7626 18.8232 18.2374 18.5303 18.5303C18.2374 18.8232 17.7626 18.8232 17.4697 18.5303L12 13.0607L6.53033 18.5303C6.23744 18.8232 5.76256 18.8232 5.46967 18.5303C5.17678 18.2374 5.17678 17.7626 5.46967 17.4697L10.9393 12L5.46967 6.53033C5.17678 6.23744 5.17678 5.76256 5.46967 5.46967Z" fill="currentColor"/>
        </svg>
      </button>
    `;

      const header = document.querySelector('header');

      if (header && header.parentNode) {
        header.parentNode.insertBefore(banner, header);
      } else {
        document.body.prepend(banner);
      }

      const closeBtn = banner.querySelector('#affiliate-banner-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          sessionStorage.setItem('affiliate-location-banner-dismissed', '1');
          banner.remove();
        });
      }
    }
  }

  // Update the content of the affiliate banner with the names of the locations associated with the affiliate.
  static updateAffiliateBannerContent() {
    const bannerText = document.getElementById('affiliate-location-banner-text');
    if (!bannerText) return;

    const locations = this._affiliateData?.locations || [];

    // affiliate but NO locations
    if (locations.length === 0) {
      bannerText.textContent = 'This affiliate is not associated with any locations.';
      return;
    }

    const names = locations
      .map((loc) => loc.companyName)
      .filter(Boolean)
      .map((name) => escapeHtml(name));

    bannerText.innerHTML = `<span class="font-bold">${this.formatNameList(names)}</span>`;
  }

  /**
   * Format a list of names into a human-readable string with proper commas and "and" conjunction.
   * Examples:
   * - ["A"] => "A"
   * - ["A", "B"] => "A and B"
   * - ["A", "B", "C"] => "A, B, and C"
   */
  static formatNameList(list) {
    if (list.length === 1) return list[0];

    if (list.length === 2) return `${list[0]} and ${list[1]}`;

    return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
  }
}

// Expose globally for use in inline scripts and other modules
window.AffiliateFlow = AffiliateFlow;
AffiliateFlow.init();
